/**
 * Shared pitch math for the moves engine. Uses Tonal for note<->MIDI
 * conversion (per MOVES-SPEC.md) and the existing chord-helpers root/interval
 * math (getChordRootMidi/getChordIntervals) so a move's output plays back
 * exactly like any other chord on the canvas.
 *
 * MODELING CONSTRAINT (read before touching moves 1, 6, or 10):
 * `Chord.scaleDegree` is a diatonic scale STEP (1-7); the actual root pitch
 * is always derived purely from (key, scaleDegree, mode) via
 * getChordRootMidi — there is no field anywhere in the Chord type for a
 * chromatically altered (non-diatonic) root. Any accidental a move wants to
 * apply to a chord's ROOT (not just its quality/extensions) has to be
 * approximated by snapping to the nearest diatonic scale degree.
 * nearestScaleDegreeForPitchClass below does that snapping and reports
 * whether the match was exact, so callers can decide whether to accept the
 * approximation or refuse to apply.
 */
import { Note } from 'tonal';
import { getChordRootMidi, getChordIntervals } from '@/utils/chord-helpers';
import type { Chord, MusicalKey, Mode, ScaleDegree } from '@/types/chord';

const ROMAN_NUMERALS_MAJOR: Record<number, string> = {
  1: 'I', 2: 'ii', 3: 'iii', 4: 'IV', 5: 'V', 6: 'vi', 7: 'vii°',
};
const ROMAN_NUMERALS_MINOR: Record<number, string> = {
  1: 'i', 2: 'ii°', 3: 'III', 4: 'iv', 5: 'V', 6: 'VI', 7: 'VII',
};

/** Roman numeral label for a scale degree — mirrors ChordShape.tsx's canvas convention. */
export function romanNumeral(scaleDegree: ScaleDegree, mode: Mode): string {
  return mode === 'major'
    ? (ROMAN_NUMERALS_MAJOR[scaleDegree] ?? '?')
    : (ROMAN_NUMERALS_MINOR[scaleDegree] ?? '?');
}

/** Pitch class (0-11) of a chord's diatonic root, per the existing scaleDegree/key/mode convention. */
export function rootPitchClass(key: MusicalKey, scaleDegree: ScaleDegree, mode: Mode): number {
  return ((getChordRootMidi(key, scaleDegree, mode) % 12) + 12) % 12;
}

/** Pitch classes (0-11, deduped) sounded by a chord: root + quality/extension intervals. */
export function chordPitchClasses(chord: Chord): number[] {
  const root = rootPitchClass(chord.key, chord.scaleDegree, chord.mode);
  const intervals = getChordIntervals(chord.quality, chord.extensions);
  return [...new Set(intervals.map((i) => (root + i) % 12))];
}

/**
 * Nearest diatonic scale degree (1-7) whose plain root matches a target
 * pitch class, plus whether that match is exact. When it isn't, the caller
 * is looking at a genuinely chromatic (non-diatonic) target root that this
 * Chord model cannot represent losslessly.
 */
export function nearestScaleDegreeForPitchClass(
  targetPc: number,
  key: MusicalKey,
  mode: Mode
): { degree: ScaleDegree; exact: boolean } {
  let best: { degree: ScaleDegree; dist: number } | null = null;
  for (let d = 1; d <= 7; d++) {
    const degree = d as ScaleDegree;
    const pc = rootPitchClass(key, degree, mode);
    const dist = Math.min((pc - targetPc + 12) % 12, (targetPc - pc + 12) % 12);
    if (!best || dist < best.dist) {
      best = { degree, dist };
    }
  }
  return { degree: best!.degree, exact: best!.dist === 0 };
}

/** Nearest absolute MIDI note whose pitch class is in `pitchClasses`, closest to `targetMidi`. */
export function nearestPitchClassMidi(pitchClasses: number[], targetMidi: number): number {
  let best: { midi: number; dist: number } | null = null;
  for (let midi = targetMidi - 12; midi <= targetMidi + 12; midi++) {
    const pc = ((midi % 12) + 12) % 12;
    if (pitchClasses.includes(pc)) {
      const dist = Math.abs(midi - targetMidi);
      if (!best || dist < best.dist) {
        best = { midi, dist };
      }
    }
  }
  return best!.midi;
}

export function noteToMidi(note: string): number {
  const midi = Note.midi(note);
  if (midi === null) {
    throw new Error(`Invalid note name: ${note}`);
  }
  return midi;
}

export function midiToNote(midi: number): string {
  const note = Note.fromMidi(midi);
  if (!note) {
    throw new Error(`Invalid MIDI number: ${midi}`);
  }
  return note;
}
